import { cn } from '@/lib/utils';
import { Globe, ArrowRight } from 'lucide-react';

/** Where the promo links to. */
const BCH_LAUNCH_URL = 'https://bchlaunch.com';

interface BchLaunchPromoProps {
  className?: string;
}

/**
 * Clean, unobtrusive promotion for BCH Launch — a service that builds custom
 * websites paid entirely in Bitcoin Cash. Rendered on the main page and on
 * individual business listing pages.
 *
 * Solid BCH-green banner with a subtle animated shimmer sweep (respects
 * prefers-reduced-motion), styled to feel on-brand rather than intrusive.
 */
export function BchLaunchPromo({ className }: BchLaunchPromoProps) {
  return (
    <a
      href={BCH_LAUNCH_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'bch-shimmer group relative flex items-center gap-4 overflow-hidden rounded-xl',
        'bg-gradient-to-br from-bch via-bch to-emerald-600 text-bch-foreground shadow-sm',
        'px-4 py-3.5 transition-shadow hover:shadow-md hover:shadow-bch/20',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bch/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className,
      )}
    >
      <div className="relative z-10 flex items-center justify-center size-10 shrink-0 rounded-lg bg-white/20 text-bch-foreground">
        <Globe className="size-5" />
      </div>

      <div className="relative z-10 min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-sm">Need a Website?</span>
          <span className="hidden sm:inline text-[11px] font-semibold text-bch-foreground bg-white/20 rounded-full px-1.5 py-0.5">
            from $99
          </span>
        </div>
        <p className="text-xs text-bch-foreground/90 leading-snug mt-0.5">
          Get a custom website built for your business. Pay entirely in Bitcoin Cash.
          <span className="sm:hidden"> Starting at $99.</span>
        </p>
      </div>

      <div className="relative z-10 flex items-center gap-1 shrink-0 text-xs font-semibold text-bch-foreground">
        <span className="hidden sm:inline">BCH Launch</span>
        <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
      </div>
    </a>
  );
}
